// import external libraries
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// import constants
import { BACKLOG_PREFERENCES_STORAGE_KEY } from './Backlog.consts';

// import types
import type {
  BacklogPreferencesState,
  BacklogPreferencesStore
} from './Backlog.types';

/**
 * What the takenlijst opens on until the planner changes something: no narrowing filters, the signed
 * difference in the cells, and both the totals column and the fulfilled weeks shown. The same defaults
 * the page has always had — now the starting point a first visit falls back to, and what a reset
 * restores to.
 */
export const BACKLOG_PREFERENCES_DEFAULTS: BacklogPreferencesState = {
  activityCodes: [],
  careReceiverIds: [],
  cellValueMode: 'difference',
  showTotals: true,
  showCompleted: true
};

/**
 * Remembers the filter and view-settings popovers across visits, so a planner does not set them again
 * every time the page opens. Persisted to localStorage.
 *
 * A remembered activity or care receiver code is safe to keep even after it leaves the data: the page
 * matches the stored ids against whatever the current response offers, so a code that no longer appears
 * simply stops narrowing rather than hiding everything.
 */
export const useBacklogPreferencesStore = create<BacklogPreferencesStore>()(
  persist(
    (set) => ({
      ...BACKLOG_PREFERENCES_DEFAULTS,
      setActivityCodes: (activityCodes) => set({ activityCodes }),
      setCareReceiverIds: (careReceiverIds) => set({ careReceiverIds }),
      setCellValueMode: (cellValueMode) => set({ cellValueMode }),
      setShowTotals: (showTotals) => set({ showTotals }),
      setShowCompleted: (showCompleted) => set({ showCompleted })
    }),
    {
      name: BACKLOG_PREFERENCES_STORAGE_KEY,
      version: 1,
      // Only the settings, never the setters — persisting the whole state would write functions that
      // JSON drops anyway.
      partialize: ({
        activityCodes,
        careReceiverIds,
        cellValueMode,
        showTotals,
        showCompleted
      }) => ({
        activityCodes,
        careReceiverIds,
        cellValueMode,
        showTotals,
        showCompleted
      })
    }
  )
);
